import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store, select } from '@ngrx/store';
import { UserStoreService } from '@polaris-customerweb/sharedcomponents.core';
import { ImageCdnSrcDirective } from '@polaris-customerweb/sharedcomponents.shared';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Observable, combineLatest } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { AppState } from 'src/app/models/app-state';
import { RideTypeService } from 'src/app/services/ride-type.service';
import { selectIsShowingSatellite } from 'src/app/state/map/layer-visibility/layer-visibility.reducer';
import { TrailLegendComponent } from '../trail-legend/trail-legend.component';

@Component({
    imports: [AngularSvgIconModule, ImageCdnSrcDirective, NgClass],
    selector: 'rdx-trail-legend-button',
    styleUrls: ['./trail-legend-button.component.scss'],
    templateUrl: './trail-legend-button.component.html',
})
export class TrailLegendButtonComponent implements OnDestroy, OnInit {
    legendExpanded = false;
    isSatellite$: Observable<boolean>;
    isOrv: boolean;
    legendUrl: string;
    vehicleType: string;
    ohmLineImg: string;
    openRideAreaImg: string;
    atvLineImg: string;
    rov50LineImg: string;
    rovLineImg: string;
    otherLineImg: string;
    designatedPavedImg: string;
    designatedUnpavedImg: string;
    closedImg: string;
    trunkImg: string;
    trailImg: string;
    clubTrailImg: string;
    unmaintainedImg: string;

    private largeLegendUrl: string;
    private isDestroyed = false;

    constructor(
        private matDialog: MatDialog,
        private store: Store<AppState>,
        private rideTypeService: RideTypeService,
        private userStoreService: UserStoreService
    ) {}

    ngOnInit() {
        this.legendExpanded = Boolean(
            this.userStoreService.get('isLegendOpen')
        );
        combineLatest([
            this.store.pipe(select(selectIsShowingSatellite)),
            this.rideTypeService.rideType$,
        ])
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(([isSatellite, selectedRideType]) => {
                const rideType = (selectedRideType || 'orv').toLowerCase();
                this.isOrv = rideType === 'orv';
                this.closedImg =
                    '/assets/icons/map-legend-icons/icon-closed-trail.svg';

                if (isSatellite) {
                    if (this.isOrv) {
                        this.otherLineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-other-sat.svg';
                        this.rovLineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-rov-sat.svg';
                        this.rov50LineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-rov50-sat.svg';
                        this.atvLineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-atv-sat.svg';
                        this.ohmLineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-ohm-sat.svg';
                        this.designatedPavedImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrails-des-paved-sat.svg';
                        this.designatedUnpavedImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrails-des-unpaved-sat.svg';
                        this.openRideAreaImg =
                            '/assets/icons/open-ride-area-off-road-sm-sat.svg';
                    } else {
                        this.trunkImg =
                            '/assets/icons/map-legend-icons/sno/icon-snowtrails-trunk-sat.svg';
                        this.trailImg =
                            '/assets/icons/map-legend-icons/sno/icon-snowtrails-trail-sat.svg';
                        this.clubTrailImg =
                            '/assets/icons/map-legend-icons/sno/icon-snowtrails-clubtrail-sat.svg';
                        this.unmaintainedImg =
                            '/assets/icons/map-legend-icons/sno/icon-snowtrails-unmaintained-sat.svg';
                        this.openRideAreaImg =
                            '/assets/icons/open-ride-area-snow-sm-sat.svg';
                    }
                } else {
                    if (this.isOrv) {
                        this.otherLineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-other.svg';
                        this.rovLineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-rov.svg';
                        this.rov50LineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-rov50.svg';
                        this.atvLineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-atv.svg';
                        this.ohmLineImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrailsmini-ohm.svg';
                        this.designatedPavedImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrails-des-paved.svg';
                        this.designatedUnpavedImg =
                            '/assets/icons/map-legend-icons/orv/icon-offroadtrails-des-unpaved.svg';
                        this.openRideAreaImg =
                            '/assets/icons/open-ride-area-off-road-sm.svg';
                    } else {
                        this.trunkImg =
                            '/assets/icons/map-legend-icons/sno/icon-snowtrails-trunk.svg';
                        this.trailImg =
                            '/assets/icons/map-legend-icons/sno/icon-snowtrails-trail.svg';
                        this.clubTrailImg =
                            '/assets/icons/map-legend-icons/sno/icon-snowtrails-clubtrail.svg';
                        this.unmaintainedImg =
                            '/assets/icons/map-legend-icons/sno/icon-snowtrails-unmaintained.svg';
                        this.openRideAreaImg =
                            '/assets/icons/open-ride-area-snow-sm.svg';
                    }
                }
            });
    }

    ngOnDestroy() {
        this.isDestroyed = true;
    }

    toggleLegendExpanded() {
        this.legendExpanded = !this.legendExpanded;
        this.userStoreService.set('isLegendOpen', this.legendExpanded);
    }

    setLegendExpandedOff() {
        this.legendExpanded = false;
        this.userStoreService.set('isLegendOpen', false);
    }

    openMapLegend() {
        const dialog = this.matDialog.open(TrailLegendComponent);
        dialog.componentInstance.legendUrl = this.largeLegendUrl;
    }
}
